import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  Merchant,
  Profile,
  FeeProfile,
  RiskProfileDropdownItem,
} from '@olv/ui-shared/models/merchant.model';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { MerchantService, MerchantUpdateDto } from '@olv/ui-shared/services/merchant.service';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountPhotoComponent } from '@olv/ui-shared/features/user-management';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { ConfirmDialogComponent } from '@olv/ui-shared/ui-shared/confirm-dialog/confirm-dialog.component';
import { Observable, finalize } from 'rxjs';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-merchant-edit-form',
  standalone: true,
  imports: [
    FormContainerComponent,
    TextInputComponent,
    DropdownComponent,
    FormsModule,
    HasPermissionDirective,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    ButtonComponent,
    MatIconModule
],
  templateUrl: './merchant-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-edit-form.component.scss'],
})
export class MerchantEditFormComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  @Input() merchant!: Merchant;
  @Input() merchantId!: string;
  @Input() logoBase64?: string;
  @Output() statusChanged = new EventEmitter<void>();
  @Output() infoChanged = new EventEmitter<void>();
  @Output() logoUpdated = new EventEmitter<string>();

  private formErrorService = inject(FormErrorService);
  private fb = inject(FormBuilder);
  private merchantService = inject(MerchantService);
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private toastrService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  merchantProfiles: Profile[] = [];
  feeProfiles: FeeProfile[] = [];
  riskProfiles: RiskProfileDropdownItem[] = [];
  isUploading = false;

  merchantProfileOptions: { value: Profile | null; label: string }[] = [];
  feeProfileOptions: { value: FeeProfile | null; label: string }[] = [];
  riskProfileOptions: { value: RiskProfileDropdownItem | null; label: string }[] = [];

  selectedMerchantProfile!: Profile | null;
  selectedFeeProfile!: FeeProfile | null;
  selectedRiskProfile!: RiskProfileDropdownItem | null;

  // Store original values for comparison
  private originalValues: {
    name: string;
    profileId?: string | null;
    feeProfileId?: string | null;
    riskProfileId?: string | null;
    supervisorEmail?: string;
    callbackUrl?: string;
    isAssetFundingMerchant: boolean;
  } = {
    name: '',
    profileId: null,
    feeProfileId: null,
    riskProfileId: null,
    supervisorEmail: '',
    callbackUrl: '',
    isAssetFundingMerchant: false,
  };

  editMerchantForm!: FormGroup;

  get canEdit(): boolean {
    return this.auth.hasPermission(Resources.Merchants, Scopes.Update);
  }

  get canReadFeeProfiles(): boolean {
    return this.auth.hasPermission(Resources.MerchantFeeProfiles, Scopes.Read);
  }

  get canReadRiskProfiles(): boolean {
    return this.auth.hasPermission(Resources.RiskProfiles, Scopes.Read);
  }

  ngOnInit(): void {
    this.selectedMerchantProfile = this.merchant.profile;
    this.selectedFeeProfile = this.merchant.feeProfile;
    this.selectedRiskProfile = this.merchant.riskProfile ?? null;

    // Store original values
    this.originalValues = {
      name: this.merchant.name,
      profileId: this.merchant.profile?.id ?? null,
      feeProfileId: this.merchant.feeProfile?.id ?? null,
      riskProfileId: this.merchant.riskProfile?.id ?? null,
      callbackUrl: this.merchant.callbackUrl,
      supervisorEmail: this.merchant.supervisorEmail,
      isAssetFundingMerchant: this.merchant.isAssetFundingMerchant ?? false,
    };
    if (this.canReadFeeProfiles) {
      this.loadFeeProfiles();
    }
    if (this.canReadRiskProfiles) {
      this.loadRiskProfiles();
    }
    this.initForm();
  }

  initForm(): void {
    this.editMerchantForm = this.fb.group({
      merchantName: [this.merchant.name, [Validators.required, ValidationService.textValidator(2, 100)]],
      callbackURL: [this.merchant.callbackUrl ?? '', [ValidationService.urlValidator()]],
      supervisorEmail: [this.merchant.supervisorEmail ?? '', [ValidationService.emailValidator()]],
      isAssetFundingMerchant: [this.merchant.isAssetFundingMerchant ?? false],
    });
  }

  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.editMerchantForm.get(controlName), fieldName);
  }

  private loadFeeProfiles(): void {
    this.merchantService.getFeeProfiles().subscribe({
      next: (response) => {
        this.merchantProfiles = response.merchantProfiles;
        this.feeProfiles = response.feeProfiles;

        // map into `{ value, label }` arrays
        this.merchantProfileOptions = this.merchantProfiles.map((p) => ({
          value: p,
          label: p.profileName,
        }));
        this.feeProfileOptions = this.feeProfiles.map((fp) => ({
          value: fp,
          label: fp.profileName,
        }));
      },
    });
  }

  private loadRiskProfiles(): void {
    this.merchantService.getAssignableRiskProfiles().subscribe({
      next: (riskProfiles) => {
        this.riskProfiles = riskProfiles;
        this.riskProfileOptions = riskProfiles.map((rp) => ({
          value: rp,
          label: rp.profileName,
        }));

        // The dropdown matches the selected value by reference, so point it at the loaded option instance.
        const assignedId = this.merchant.riskProfile?.id;
        if (assignedId) {
          this.selectedRiskProfile = riskProfiles.find((rp) => rp.id === assignedId) ?? this.selectedRiskProfile;
        }
      },
    });
  }

  get logoText(): string {
    if (!this.merchant?.name) {
      return '';
    }
    const parts = this.merchant.name.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  get isUpdateDisabled(): boolean {
    if (!this.editMerchantForm) {
      return true;
    }

    if (this.editMerchantForm.invalid) {
      return true;
    }

    const name = this.editMerchantForm.value.merchantName?.trim() ?? '';
    const callbackUrl = this.editMerchantForm.value.callbackURL?.trim() ?? '';
    const supervisorEmail = this.editMerchantForm.value.supervisorEmail?.trim() ?? '';
    const isAssetFundingMerchant = this.editMerchantForm.value.isAssetFundingMerchant ?? false;

    const comparisons = [
      [name, this.originalValues.name],
      [callbackUrl, this.originalValues.callbackUrl ?? ''],
      [supervisorEmail, this.originalValues.supervisorEmail ?? ''],
      [isAssetFundingMerchant, this.originalValues.isAssetFundingMerchant],
      [this.selectedMerchantProfile?.id ?? null, this.originalValues.profileId ?? null],
      [this.selectedFeeProfile?.id ?? null, this.originalValues.feeProfileId ?? null],
      [this.selectedRiskProfile?.id ?? null, this.originalValues.riskProfileId ?? null],
    ];

    return comparisons.every(([a, b]) => a === b);
  }

  get updateButtonState(): 'disabled' | 'default' {
    return this.isUpdateDisabled || this.isUploading ? 'disabled' : 'default';
  }

  triggerLogoUpload(): void {
    const dialogRef = this.dialog.open(AccountPhotoComponent, {
      width: '600px',
      maxHeight: '95vh',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((file: File | undefined) => {
      if (!file) return;
      this.saveCroppedImage(file);
    });
  }

  saveCroppedImage(file: File): void {
    this.merchantService.updateMerchantLogo(this.merchantId, file).subscribe({
      next: (fileResp) => {
        this.logoUpdated.emit(fileResp.id);
      },
      error: (error) => {
        console.error('Error uploading logo:', error);
      },
    });
  }

  protected statusUpdate(): void {
    if (!this.merchantId) return;

    // merchant.isActive is the CURRENT status, so a click while active is the disable direction.
    const isDisabling = this.merchant.isActive;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Change Status',
        // Disabling clears the merchant's delegation allocations, and re-enabling does not bring them
        // back - the operator confirms that loss, not just a status flip.
        message: isDisabling
          ? "The merchant will be disabled and its allocations will be reset to 0. Re-enabling will not restore them."
          : 'The merchant status will be changed',
        confirmText: isDisabling ? 'Disable' : 'Activate',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.isUploading = true;
      this.merchantService
        .updateMerchantStatus(this.merchantId, this.merchant.isActive)
        .pipe(
          finalize(() => (this.isUploading = false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            this.statusChanged.emit();
            this.toastrService.success('Status updated successfully');
          },
          error: () => this.toastrService.error('Error updating status'),
        });
    });
  }

  sweepingStatusUpdate() {
    if (!this.merchantId) return;

    this.runOtpVerifiedUpdate({
      title: 'Verify Sweeping Change',
      run: (otp) =>
        this.merchantService.updateMerchantSweepingStatus(this.merchantId, this.merchant.isSweepingEnabled, otp),
      successMessage: 'Sweeping status updated successfully',
      errorMessage: 'Failed to update sweeping status',
    });
  }

  // Both status toggles are the same flow around a different call, and the OTP-rejected branch has to be right
  // in every one of them - kept in one place so a third copy cannot drift away from the other two.
  private runOtpVerifiedUpdate(flow: {
    title: string;
    subtitle?: string;
    run: (otp: string) => Observable<void>;
    successMessage: string;
    errorMessage: string;
  }): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: { title: flow.title, subtitle: flow.subtitle },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) =>
      this.submitOtpVerifiedUpdate(otp, dialogRef, flow),
    );

    dialogRef.afterClosed().subscribe(() => sub.unsubscribe());
  }

  private submitOtpVerifiedUpdate(
    otp: string,
    dialogRef: MatDialogRef<OtpVerificationDialogComponent>,
    flow: { run: (otp: string) => Observable<void>; successMessage: string; errorMessage: string },
  ): void {
    if (!this.merchantId) return;
    this.isUploading = true;

    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    flow
      .run(otp)
      .pipe(
        finalize(() => {
          this.isUploading = false;
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          dialogRef.close();
          this.statusChanged.emit();
          this.toastrService.success(flow.successMessage);
        },
        error: (err) => {
          // A rejected code keeps the dialog open for another attempt; anything else has nothing to retry.
          if (err.isOtpError) {
            dialog.setErrorState(err.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          this.toastrService.error(flow.errorMessage);
        },
      });
  }

  /**
   * Get only the changed fields
   */
  private getChangedFields(): Partial<MerchantUpdateDto> {
    const changes: Partial<MerchantUpdateDto> = {};

    const currentName = this.editMerchantForm.value.merchantName?.trim() || '';
    const currentProfileId = this.selectedMerchantProfile?.id;
    const currentFeeProfileId = this.selectedFeeProfile?.id;
    const currentRiskProfileId = this.selectedRiskProfile?.id;
    const currentCallbackUrl = this.editMerchantForm.value.callbackURL?.trim() || '';
    const currentSupervisorEmail = this.editMerchantForm.value.supervisorEmail?.trim() || '';
    const currentIsAssetFundingMerchant = this.editMerchantForm.value.isAssetFundingMerchant ?? false;

    if (currentName !== this.originalValues.name) {
      changes.name = currentName;
    }

    if (currentProfileId !== this.originalValues.profileId) {
      changes.merchantProfileId = currentProfileId;
    }

    if (currentFeeProfileId !== this.originalValues.feeProfileId) {
      changes.merchantFeeProfileId = currentFeeProfileId;
    }

    if (currentRiskProfileId !== this.originalValues.riskProfileId) {
      changes.riskProfileId = currentRiskProfileId;
    }

    if (currentCallbackUrl !== (this.originalValues.callbackUrl ?? '')) {
      changes.callbackUrl = currentCallbackUrl;
    }

    if (currentSupervisorEmail !== (this.originalValues.supervisorEmail ?? '')) {
      changes.supervisorEmail = currentSupervisorEmail;
    }

    if (currentIsAssetFundingMerchant !== this.originalValues.isAssetFundingMerchant) {
      changes.isAssetFundingMerchant = currentIsAssetFundingMerchant;
    }

    return changes;
  }

  onSubmit(): void {
    if (this.isUpdateDisabled) return;

    this.isUploading = true;

    const changedFields = this.getChangedFields();

    // Only proceed if there are actual changes
    if (Object.keys(changedFields).length === 0) {
      this.isUploading = false;
      return;
    }

    this.merchantService.updateMerchantPartial(this.merchant.id, changedFields).subscribe({
      next: (updated) => {
        this.isUploading = false;
        // Update the original values after successful update
        this.originalValues = {
          name: updated.name,
          profileId: updated.profile?.id || null,
          feeProfileId: updated.feeProfile?.id || null,
          riskProfileId: updated.riskProfile?.id || null,
          callbackUrl: updated.callbackUrl,
          supervisorEmail: updated.supervisorEmail,
          isAssetFundingMerchant: updated.isAssetFundingMerchant ?? false,
        };
        this.infoChanged.emit();
      },
      error: () => {
        this.isUploading = false;
      },
    });
  }
}
